import { useState, useEffect, useCallback } from "react";
import { Bitcoin, Landmark, Plug, Plus, Pencil, Trash2, KeyRound, ArrowUpFromLine } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const TABS = [["crypto", "Crypto Wallets", Bitcoin], ["bank", "Bank Accounts", Landmark], ["api", "Payment APIs", Plug], ["withdrawal", "Withdrawal Methods", ArrowUpFromLine]];
const PROVIDER_FIELDS = {
  stripe: ["secret_key", "publishable_key"],
  paystack: ["secret_key", "public_key"],
  flutterwave: ["secret_key", "public_key", "encryption_key"],
  squad: ["secret_key", "public_key"],
  custom: ["api_key", "secret_key"],
};
const EMPTY = {
  crypto: { type: "crypto", enabled: true, coin: "", network: "", address: "", label: "", instructions: "" },
  bank: { type: "bank", enabled: true, bank_name: "", account_number: "", account_name: "", routing_code: "", swift: "", currency: "USD", label: "", instructions: "" },
  api: { type: "api", enabled: true, provider: "paystack", display_name: "", credentials: {} },
  withdrawal: { type: "withdrawal", kind: "bank", enabled: true, label: "", coin: "", network: "", instructions: "", fee_fixed: 0, fee_percent: 0 },
};
const money = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const limits = (m) => [m.min_amount ? `min ${money(m.min_amount)}` : "", m.max_amount ? `max ${money(m.max_amount)}` : ""].filter(Boolean).join(" · ");
const fees = (m) => [m.fee_fixed ? money(m.fee_fixed) : "", m.fee_percent ? `${m.fee_percent}%` : ""].filter(Boolean).join(" + ") || "no fee";

function NumF({ label, k, form, set, ph, step = "0.01" }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" step={step} value={form[k] ?? ""} placeholder={ph} onChange={(e) => set({ ...form, [k]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`pm-field-${k}`} className={clsx(inp, "font-mono")} />
    </div>
  );
}
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
const inp = "bg-fx-base border-white/10";

function F({ label, k, form, set, mono, ph, req }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}{req && <span className="text-neg"> *</span>}</Label>
      <Input value={form[k] || ""} onChange={(e) => set({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`pm-field-${k}`} className={clsx(inp, mono && "font-mono text-xs")} />
    </div>
  );
}

function CredEditor({ form, set, existing }) {
  const fields = form.provider === "custom" ? [...new Set([...(existing || []).map((c) => c.name), ...Object.keys(form.credentials), ...PROVIDER_FIELDS.custom])] : PROVIDER_FIELDS[form.provider];
  const [extra, setExtra] = useState("");
  const masked = Object.fromEntries((existing || []).map((c) => [c.name, c.masked]));
  return (
    <div className="space-y-2 rounded-lg bg-fx-base border border-white/5 p-3">
      <div className="flex items-center gap-1.5 text-xs text-fx-s3"><KeyRound className="h-3.5 w-3.5" /> API credentials, stored encrypted. Leave blank to keep the current value.</div>
      {fields.map((k) => (
        <div key={k} className="space-y-1">
          <Label className="text-xs font-mono">{k}{masked[k] && <span className="text-fx-s3 ml-2">current: {masked[k]}</span>}</Label>
          <Input type="password" autoComplete="new-password" value={form.credentials[k] === "__keep__" ? "" : form.credentials[k] || ""} placeholder={masked[k] ? "Unchanged" : "Enter value"}
            onChange={(e) => set({ ...form, credentials: { ...form.credentials, [k]: e.target.value } })} data-testid={`pm-cred-${k}`} className={clsx(inp, "font-mono text-xs h-8")} />
        </div>
      ))}
      {form.provider === "custom" && (
        <div className="flex gap-2 pt-1">
          <Input value={extra} onChange={(e) => setExtra(e.target.value.replace(/\s/g, "_"))} placeholder="Add credential field (e.g. merchant_id)" data-testid="pm-cred-new-name" className={clsx(inp, "h-8 text-xs font-mono")} />
          <Button size="sm" disabled={!extra} onClick={() => { set({ ...form, credentials: { ...form.credentials, [extra]: "" } }); setExtra(""); }} data-testid="pm-cred-add" className="h-8 bg-brandblue hover:bg-blue-500">Add</Button>
        </div>
      )}
    </div>
  );
}

function MethodDialog({ form, setForm, existing, onSaved }) {
  const [busy, setBusy] = useState(false);
  if (!form) return null;
  const isNew = !form.id;
  const save = async () => {
    setBusy(true);
    const body = { ...form, credentials: form.type === "api" ? Object.fromEntries(Object.entries(form.credentials).filter(([, v]) => v && v.trim())) : {} };
    try {
      if (isNew) await api.post("/admin/payment-methods", body); else await api.put(`/admin/payment-methods/${form.id}`, body);
      toast.success("Payment method saved"); setForm(null); onSaved();
    } catch (e) { err(e); }
    setBusy(false);
  };
  const t = form.type;
  return (
    <Dialog open onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pm-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Add" : "Edit"} {t === "crypto" ? "crypto wallet" : t === "bank" ? "bank account" : t === "withdrawal" ? "withdrawal method" : "payment API"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {t === "crypto" && <>
            <div className="grid grid-cols-2 gap-3"><F label="Coin" k="coin" form={form} set={setForm} ph="USDT" req /><F label="Network" k="network" form={form} set={setForm} ph="TRC20" req /></div>
            <F label="Wallet address" k="address" form={form} set={setForm} mono ph="Paste the receiving wallet address" req />
            <p className="text-[11px] text-fx-s3">The QR code users scan is generated automatically from this address.</p>
          </>}
          {t === "bank" && <>
            <F label="Bank name" k="bank_name" form={form} set={setForm} req />
            <div className="grid grid-cols-2 gap-3"><F label="Account number" k="account_number" form={form} set={setForm} mono req /><F label="Account name" k="account_name" form={form} set={setForm} req /></div>
            <div className="grid grid-cols-3 gap-3"><F label="Routing / Sort code" k="routing_code" form={form} set={setForm} mono /><F label="SWIFT / BIC" k="swift" form={form} set={setForm} mono /><F label="Currency" k="currency" form={form} set={setForm} /></div>
          </>}
          {t === "api" && <>
            <div className="space-y-1">
              <Label className="text-xs">Processor</Label>
              <select value={form.provider} disabled={!isNew} onChange={(e) => setForm({ ...form, provider: e.target.value, credentials: {} })} data-testid="pm-provider-select" className="w-full h-9 rounded-md bg-fx-base border border-white/10 px-3 text-sm capitalize disabled:opacity-60">
                {Object.keys(PROVIDER_FIELDS).map((p) => <option key={p} value={p}>{p === "custom" ? "Custom (stored for reference only)" : p}</option>)}
              </select>
            </div>
            <F label={form.provider === "custom" ? "Processor name" : "Display name"} k="display_name" form={form} set={setForm} ph={form.provider === "custom" ? "e.g. Coinbase Commerce" : "Shown to users"} req={form.provider === "custom"} />
            {form.provider === "custom" && <p className="text-[11px] text-gold">Custom processors aren't connected to checkout. Their credentials are stored securely for reference only and aren't shown to users.</p>}
            <CredEditor form={form} set={setForm} existing={existing} />
          </>}
          {t === "withdrawal" && <>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {[["bank", "Bank transfer"], ["crypto", "Crypto"]].map(([k, l]) => (
                  <button key={k} onClick={() => setForm({ ...form, kind: k })} data-testid={`pm-kind-${k}`} className={clsx("h-9 rounded-md border text-sm", form.kind === k ? "bg-brandblue/20 border-brandblue" : "border-white/10 text-fx-s3")}>{l}</button>
                ))}
              </div>
            </div>
            {form.kind === "crypto" && <div className="grid grid-cols-2 gap-3"><F label="Coin" k="coin" form={form} set={setForm} ph="USDT" req /><F label="Network" k="network" form={form} set={setForm} ph="TRC20" req /></div>}
            <p className="text-[11px] text-fx-s3">{form.kind === "crypto" ? "Users enter their own wallet address for this coin and network." : "Users enter their bank name, account name, account number and routing/sort code."}</p>
            <div className="grid grid-cols-2 gap-3"><NumF label="Fixed fee (USD)" k="fee_fixed" form={form} set={setForm} ph="0" /><NumF label="Percentage fee (%)" k="fee_percent" form={form} set={setForm} ph="0" step="0.1" /></div>
          </>}
          <div className="grid grid-cols-2 gap-3">
            <NumF label={`Minimum ${t === "withdrawal" ? "withdrawal" : "deposit"} (USD)`} k="min_amount" form={form} set={setForm} ph="No minimum" />
            <NumF label={`Maximum ${t === "withdrawal" ? "withdrawal" : "deposit"} (USD)`} k="max_amount" form={form} set={setForm} ph="No maximum" />
          </div>
          {t === "withdrawal" && <p className="text-[11px] text-fx-s3 -mt-1">The platform minimum withdrawal is $10. Lower minimums are raised to $10.</p>}
          {t !== "api" && <>
            <F label={t === "withdrawal" ? "Display name" : "Display label (optional)"} k="label" form={form} set={setForm} ph={t === "withdrawal" ? (form.kind === "crypto" ? "e.g. USDT (TRC20), auto if blank" : "e.g. Bank Transfer, auto if blank") : t === "crypto" ? "e.g. USDT (Tron)" : "e.g. US Bank Transfer"} />
            <div className="space-y-1"><Label className="text-xs">Instructions for users (optional)</Label><Textarea value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="pm-field-instructions" className={clsx(inp, "min-h-[60px] text-sm")} /></div>
          </>}
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="pm-field-enabled" /> Enabled for users</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="pm-save" className="bg-brandblue hover:bg-blue-500">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function summary(m) {
  const lim = limits(m) ? `  ·  ${limits(m)}` : "";
  if (m.type === "withdrawal") return [m.label, `${m.kind === "crypto" ? `${m.coin} · ${m.network}` : "Bank transfer"} · fee ${fees(m)}${lim}`];
  if (m.type === "crypto") return [m.label || `${m.coin} · ${m.network}`, m.address + lim];
  if (m.type === "bank") return [m.label || m.bank_name, `${m.account_name} · ${m.account_number}${m.routing_code ? ` · ${m.routing_code}` : ""}${lim}`];
  if (lim) return [m.display_name, (m.credentials?.filter((c) => c.set).map((c) => `${c.name} ${c.masked}`).join("  ") || "No credentials") + lim];
  return [m.display_name, m.credentials?.length ? m.credentials.filter((c) => c.set).map((c) => `${c.name} ${c.masked}`).join("  ") : "No credentials"];
}

export default function AdminPaymentGateways() {
  const [tab, setTab] = useState("crypto");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/admin/payment-methods").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const list = rows.filter((r) => r.type === tab);
  const editing = form?.id ? rows.find((r) => r.id === form.id) : null;

  const toggle = async (m) => { try { await api.patch(`/admin/payment-methods/${m.id}/toggle`, { enabled: !m.enabled }); load(); } catch (e) { err(e); } };
  const del = async (m) => { if (!window.confirm(`Delete "${summary(m)[0]}"?`)) return; try { await api.delete(`/admin/payment-methods/${m.id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };
  const edit = (m) => setForm({ ...EMPTY[m.type], ...m, credentials: m.type === "api" ? {} : undefined });

  return (
    <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden" data-testid="payment-gateways">
      <div className="px-5 py-4 border-b border-white/5 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display font-bold">Payment Gateways</h2><p className="text-xs text-fx-s3">Deposit and withdrawal methods users can choose from. Only enabled methods are shown.</p></div>
        <Button onClick={() => setForm({ ...EMPTY[tab], credentials: {} })} data-testid="pm-add-btn" className="bg-brandblue hover:bg-blue-500 gap-2 h-8"><Plus className="h-4 w-4" /> Add {tab === "crypto" ? "wallet" : tab === "bank" ? "bank account" : tab === "withdrawal" ? "withdrawal method" : "integration"}</Button>
      </div>
      <div className="flex gap-1 px-5 pt-3">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`pm-tab-${k}`} className={clsx("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors", tab === k ? "bg-brandblue text-white" : "text-fx-s3 hover:text-white hover:bg-white/5")}>
            <I className="h-3.5 w-3.5" /> {l} <span className="opacity-70 font-mono">{rows.filter((r) => r.type === k).length}</span>
          </button>
        ))}
      </div>
      <div className="p-5 space-y-2">
        {list.length === 0 && <p className="text-sm text-fx-s3 py-6 text-center" data-testid="pm-empty">Nothing added yet.</p>}
        {list.map((m) => {
          const [title, sub] = summary(m);
          return (
            <div key={m.id} data-testid={`pm-row-${m.id}`} className={clsx("flex items-center gap-4 rounded-lg border p-3 transition-opacity", m.enabled ? "bg-fx-base border-white/5" : "bg-fx-base/50 border-white/5 opacity-60")}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm flex items-center gap-2">{title}
                  {m.type === "api" && <span className={clsx("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase", m.provider === "custom" ? "bg-white/5 text-fx-s3" : "bg-brandblue/15 text-brandblue")}>{m.provider === "custom" ? "reference only" : m.provider}</span>}
                </div>
                <div className="text-xs text-fx-s3 font-mono truncate">{sub}</div>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-fx-s3"><Switch checked={m.enabled} onCheckedChange={() => toggle(m)} data-testid={`pm-toggle-${m.id}`} /> {m.enabled ? "On" : "Off"}</label>
              <Button size="sm" variant="ghost" onClick={() => edit(m)} data-testid={`pm-edit-${m.id}`} className="h-7 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(m)} data-testid={`pm-delete-${m.id}`} className="h-7 px-2 text-neg"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          );
        })}
        {tab === "api" && <p className="text-[11px] text-fx-s3 pt-2">Card payments use the platform's Stripe test key until you add your own Stripe entry. Disabling a processor here hides it from users.</p>}
      </div>
      <MethodDialog form={form} setForm={setForm} existing={editing?.credentials} onSaved={load} />
    </div>
  );
}
